import { Component, type ErrorInfo, type ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import i18n from '../i18n'

interface Props {
  children: ReactNode
}

interface State {
  failed: boolean
}

/**
 * Last-resort guard around the whole app. React unmounts the entire tree when a render throws,
 * so without this a single bad field on one order card leaves the shopkeeper's phone on a
 * **blank white screen** — no message, no reload button, nothing to report. The live board is
 * the critical surface (§7), and it is the one that renders server data every few seconds.
 *
 * <p>A class is the only way: React exposes no hook equivalent of `componentDidCatch`.
 * Translation goes through the `i18n` instance directly rather than `useTranslation` for the
 * same reason — and a language switch not re-rendering this is moot, the tree is already dead.
 *
 * <p>It deliberately does not report anywhere: there is no client-error endpoint to receive it
 * (adding one is a backend feature, not this fix). `console.error` keeps the stack reachable
 * over USB devtools, which is how a real shopkeeper's phone gets debugged. ponytail: reload is
 * the only recovery offered — a render crash means the component's state is untrustworthy, and
 * a "try again" that re-renders the same bad data just crashes again.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[error-boundary] render failed:', error, info.componentStack)
  }

  render() {
    if (!this.state.failed) {
      return this.props.children
    }
    return (
      <Box sx={{ p: 2, maxWidth: 480, mx: 'auto' }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              {i18n.t('common.errorBoundary.reload')}
            </Button>
          }
        >
          <AlertTitle>{i18n.t('common.errorBoundary.title')}</AlertTitle>
          {i18n.t('common.errorBoundary.body')}
        </Alert>
      </Box>
    )
  }
}
